import { useMemo, useState } from 'react';
import { ShowPageLayout } from 'framework/ui/layout';
import {
  useNewProjectService,
  CHANGE_REQUEST_STATUS,
  DOCUSIGN_PLACEHOLDERS,
  useProjectFinanceService,
  APPROVE_CHANGE_REQUEST,
  SEND_FOR_SIGNING,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CHANGE_REQUEST_TEMPLATE_FOOTER,
  GUIDELINE_1,
  GUIDELINE_2,
  GUIDELINE_3,
  GUIDELINE_4,
} from '../mdEditor/templateConstants';
import { Box } from '@mui/material';
import { Button, Loading } from 'framework/ui/mui-builder';
import { useAsyncValue, SanitizedHtml, useSubmit } from 'framework/utils';
import { ModalForm, TextArea } from 'framework/form';
import { useEmployeeService } from 'domain/shared/employee';
import { Modal } from 'framework/ui/ui-builder';
import { ApprovalLayout } from 'framework/ui/ui-builder/approval';
import ChangeRequestBreadcrumbs from 'domain/projects/newProject/shared/breadcrumbs/ChangeRequestBreadcrumbs';
import SignButton from './SignButton';
import useChangeRequestService from '../changeRequestService';
import { useAuthorization } from 'framework/authorization';
import { useApplicationSettings } from 'framework/uiTheme';
import { markdownToHtml, PlaceholderValues, replacePlaceholders, TemplatePreview } from 'framework/markdownEditor';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';

const PreviewChangeRequestLayout = () => {
  const { id, requestId } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const { getFinanceUrl } = useNewProjectUrl();
  const { isGranted } = useAuthorization();

  const { getById, getAccountRealName } = useNewProjectService();
  const { searchSensitiveProjectInfo } = useProjectFinanceService();
  const { getChangeRequestById, approveChangeRequest, rejectChangeRequest, getChangeRequestRejectValidationSchema } =
    useChangeRequestService();
  const { getEmployeeName } = useEmployeeService();

  const { onSubmit: onApprove, isLoading: isApproving } = useSubmit({
    afterSubmit: {
      execute: () => {
        window.location.reload();
      },
    },
    submitMethod: () => approveChangeRequest(requestId!),
  });

  const { getUpdateChangeRequestUrl } = useNewProjectUrl();
  const { getUserAlias } = useAuthenticatedUserService();
  const navigate = useNavigate();
  const { useDefaultTextTransformer } = useApplicationSettings();
  const { mapAsyncTags = () => {}, mapTags = () => {} } = useDefaultTextTransformer?.() ?? {};

  const projectData = useAsyncValue(() => getById(id!));
  const changeRequestData = useAsyncValue(() => getChangeRequestById(requestId!));
  const rawContent = (changeRequestData?.rawContent || '') + CHANGE_REQUEST_TEMPLATE_FOOTER;

  const placeholderValues: Nullable<PlaceholderValues> = useAsyncValue(async () => {
    const currentUserAlias = await getUserAlias();
    if (!changeRequestData || !projectData) return null;
    const [projectManagerName, sensitiveInfo, sensitiveAccountName] = await Promise.all([
      getInitValueName(changeRequestData.createdBy ? changeRequestData.createdBy : currentUserAlias),
      searchSensitiveProjectInfo([changeRequestData.additionalBudgetAmountId]),
      getAccountRealName(id!),
    ]);

    return {
      guideline1: GUIDELINE_1,
      guideline2: GUIDELINE_2,
      guideline3: GUIDELINE_3,
      guideline4: GUIDELINE_4,
      accountName: sensitiveAccountName?.originalName || projectData.accountName,
      projectName: projectData.name,
      projectManager: projectManagerName,
      receiverFullName: changeRequestData.receiverFullName,
      receiverEmail: changeRequestData.receiverEmail,
      receiverTitle: changeRequestData.receiverTitle,
      alias: changeRequestData.alias || '-',
      title: changeRequestData.title,
      newDeliveryDate: changeRequestData.newDeliveryDate,
      additionalBudgetAmount: sensitiveInfo?.[0]?.value,
      additionalFte: changeRequestData.additionalFte,
      additionalHours: changeRequestData.additionalHours,
      companyName: changeRequestData.companyName,
      ...DOCUSIGN_PLACEHOLDERS,
    };
  }, [projectData, changeRequestData]);

  const syncData = useMemo(() => {
    if (!changeRequestData?.timeline?.[0]?.text) return '';
    return mapTags(changeRequestData.timeline[0].text);
  }, [changeRequestData, mapTags]);

  const asyncData = useAsyncValue(async () => {
    if (!syncData) return '';
    return mapAsyncTags(syncData);
  }, [syncData, mapAsyncTags]);

  const getInitValueName = async (alias: string | undefined) => {
    if (!alias) return undefined;
    return getEmployeeName(alias).then(response => `${response.firstName} ${response.lastName}`);
  };

  const html = useMemo(() => {
    if (!rawContent || !placeholderValues) return undefined;
    const replacedContent = rawContent
      .replace(/<span class='docu-sign-anchor'>(\w+)<\/span>/g, '<span style="color:grey">{{ $1 }}</span>')
      .replace(/\{\{\sguideline(\d+)\s\}\}/g, '<span style="color:grey">{{ guideline$1 }}</span>');
    const contentWithValues = replacePlaceholders(replacedContent, placeholderValues);

    return markdownToHtml(contentWithValues);
  }, [placeholderValues, rawContent]);

  if (!html) {
    return <Loading />;
  }

  const canDecide =
    isGranted(APPROVE_CHANGE_REQUEST) && changeRequestData?.status === CHANGE_REQUEST_STATUS.IN_APPROVAL;
  const showSign = isGranted(SEND_FOR_SIGNING) && changeRequestData?.status === CHANGE_REQUEST_STATUS.APPROVED;
  const isRejected = changeRequestData?.status === CHANGE_REQUEST_STATUS.REJECTED;

  const actions = showSign ? (
    <SignButton projectId={id} requestId={requestId} content={rawContent} placeholderValues={placeholderValues} />
  ) : isRejected ? (
    <>
      <Button variant="outlined" onClick={() => navigate(getFinanceUrl(id!))}>
        Cancel
      </Button>
      <Button variant="contained" onClick={() => navigate(getUpdateChangeRequestUrl(id!, requestId!))}>
        Edit
      </Button>
    </>
  ) : undefined;

  return (
    <ShowPageLayout breadcrumbs={<ChangeRequestBreadcrumbs />}>
      <ApprovalLayout
        onApprove={canDecide ? onApprove : undefined}
        onReject={canDecide ? () => setModalOpen(true) : undefined}
        isSubmitting={isApproving || isRejecting}
        actions={actions}
      >
        {(changeRequestData?.status === CHANGE_REQUEST_STATUS.REJECTED ||
          changeRequestData?.status === CHANGE_REQUEST_STATUS.APPROVED) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              my: 2,
              pb: 2,
              color: changeRequestData?.status === CHANGE_REQUEST_STATUS.REJECTED ? 'error.main' : 'text.primary',
            }}
          >
            <SanitizedHtml wrapperTag="span" html={asyncData || syncData} />
          </Box>
        )}
        <TemplatePreview
          showTitle={false}
          paperSx={{
            p: 2,
            '& h2, & h1, & h3, & h4': { margin: '20px 0 10px 0' },
          }}
          html={html}
          title=""
        />

        <Modal isOpen={modalOpen} onToggle={() => setModalOpen(false)} title="Reason for rejection">
          <ModalForm
            closeModal={() => setModalOpen(false)}
            modalOpen={modalOpen}
            getValidationSchemaFunc={getChangeRequestRejectValidationSchema}
            submitFormFunc={async values => {
              setIsRejecting(true);
              try {
                await rejectChangeRequest(requestId!, values.reason);
              } finally {
                setIsRejecting(false);
              }
            }}
            afterSubmit={{
              redirect: getFinanceUrl(id),
              message: 'You are awesome! Rejection was done.',
            }}
          >
            <TextArea source="reason" label="" placeholder="Please, provide reason here" />
          </ModalForm>
        </Modal>
      </ApprovalLayout>
    </ShowPageLayout>
  );
};
export default PreviewChangeRequestLayout;
